import React, { useCallback, useContext, useMemo } from 'react'
import { OfferPageActions, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { getCurrentDateLabel, getCurrentShortOccupancyLabel } from 'selectors/offerPage/offerPageSelectors'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import OfferCalendarSidebarDatePicker from '../../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { mobileRoomTestCalendarFilters } from '../FlashHotelFilters.constants'
import noop from 'lib/function/noop'
import FlashMobileStickyHeaderFiltersCapacityModal from './FlashMobileStickyHeaderFiltersCapacityModal'
import FlashMobileStickyHeaderFiltersCharterCalendarModal from './FlashMobileStickyHeaderFiltersCharterCalendarModal'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import CharterFlightOfferAirportModal from 'components/OfferPage/Filters/CharterFlight/CharterFlightOfferAirportModal'
import { fareCabinLabelMap, FlightsClassTypes } from 'constants/flight'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { useIntl } from 'react-intl'

interface MappedProps {
  offer: App.Offer
  dateLabel: string
  occupancyLabel: string
  airport?: App.Airport
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  priceMode: OfferPagePriceMode
  checkIn?: string
  checkOut?: string
  rooms?: Array<OfferPageRoom>
}

interface Props {
  onAfterCloseCapacity?: () => void
  ref?: React.RefObject<HTMLDivElement | null>
  // Charter calendar props
  priceMode?: 'total' | 'perPerson'
  onCharterDurationChange?: (nights: number) => void
}

function FlashMobileStickyHeaderFilters(props: Props & MappedProps) {
  const {
    offer,
    dateLabel,
    occupancyLabel,
    airport,
    fareClass,
    charterBundleRoute,
    ref,
    onAfterCloseCapacity = noop,
    priceMode,
    onCharterDurationChange,
    checkIn,
    checkOut,
    rooms,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer.packages])
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
  }, [offer.campaignTags, rooms])
  const canOpenFareClassModal = charterFareClassPolicy.allowedFareClasses.length > 1
  const shouldShowFareClassFilter = Boolean(charterBundleRoute) && canOpenFareClassModal

  const defaultCharterDurationHandler = useCallback((nights: number) => {
    // Always dispatch setDuration to update the duration state
    dispatch({
      type: OfferPageActions.setDuration,
      duration: nights,
    })
  }, [dispatch])

  const handleCalendarDurationChange = useCallback((nights: number) => {
    if (onCharterDurationChange) {
      onCharterDurationChange(nights)
      return
    }

    defaultCharterDurationHandler(nights)
  }, [onCharterDurationChange, defaultCharterDurationHandler])

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeHeaderFilter })
  }, [dispatch])

  const onCloseCapacity = useCallback(() => {
    onClose()
    onAfterCloseCapacity()
  }, [onClose, onAfterCloseCapacity])

  const onOpenCalendar = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'checkin' })

    if (charterBundleRoute && fareClass && priceMode) {
      // Charter calendar - modal fetches its own data and dispatches dates internally
      showModal(<FlashMobileStickyHeaderFiltersCharterCalendarModal
        onDurationChange={handleCalendarDurationChange}
        onClose={onClose}
      />)
    } else {
      showModal(<ModalBase mode="drawer">
        <OfferCalendarSidebarDatePicker
          useConfirmDatesFooter
          subFilters={mobileRoomTestCalendarFilters}
          onClose={onClose}
        />
      </ModalBase>)
    }
  }, [showModal, dispatch, onClose, charterBundleRoute, fareClass, priceMode, handleCalendarDurationChange])

  const onOpenCapacity = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })

    showModal(<FlashMobileStickyHeaderFiltersCapacityModal capacities={capacities} offer={offer} />).then(onCloseCapacity)
  }, [dispatch, showModal, capacities, offer, onCloseCapacity])

  const onOpenAirport = useCallback(() => {
    if (charterBundleRoute) {
      dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'airport' })
      showModal(<CharterFlightOfferAirportModal />).then(onClose)
    }
  }, [charterBundleRoute, dispatch, showModal, onClose])

  const onOpenFareClass = useCallback(async() => {
    if (charterBundleRoute) {
      if (!canOpenFareClassModal) {
        return
      }
      const selectedClass = await showModal<FlightsClassTypes>(<MaldivesCharterFlightFareClassModal
        selectedClassId={fareClass}
        checkInDate={checkIn}
        checkOutDate={checkOut}
        occupancies={rooms}
        allowedFareClasses={charterFareClassPolicy.allowedFareClasses}
      />)
      if (selectedClass) {
        dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
      }
    }
  }, [charterBundleRoute, showModal, fareClass, checkIn, checkOut, rooms, dispatch, canOpenFareClassModal, charterFareClassPolicy.allowedFareClasses])

  return (
    <HorizontalContainerWithFadedOverflow gap={8} ref={ref}>
      <FilterChip
        startIcon={<LineCalendarIcon />}
        size="medium"
        onClick={onOpenCalendar}
      >
        {dateLabel || intl.formatMessage({
          id: 'GGCj6zAWzt',
          defaultMessage: 'Anytime',
          description: 'Default date label when no date is selected',
        })}
      </FilterChip>
      {occupancyLabel && <FilterChip
        startIcon={<LineUsersAltIcon />}
        size="medium"
        onClick={onOpenCapacity}
      >
        {occupancyLabel}
      </FilterChip>}
      {charterBundleRoute && <>
        <FilterChip
          startIcon={<LinePlaneDepartureIcon />}
          size="medium"
          onClick={onOpenAirport}
        >
          {airport ? `${airport.name}, ${airport.code}` : intl.formatMessage({ id: 'coXHrtWPtT', defaultMessage: 'Departure airport', description: 'Label for departure airport filter when no airport is selected' })}
        </FilterChip>
        {shouldShowFareClassFilter && (
          <FilterChip
            startIcon={<LineSeatIcon />}
            size="medium"
            onClick={onOpenFareClass}
          >
            {fareCabinLabelMap[fareClass]}
          </FilterChip>
        )}
      </>}
    </HorizontalContainerWithFadedOverflow>
  )
}

export default withOfferPageAndReduxState((state): MappedProps => ({
  offer: state.offer!,
  dateLabel: getCurrentDateLabel(state),
  occupancyLabel: getCurrentShortOccupancyLabel(state),
  charterBundleRoute: state.charterBundleRoute,
  airport: state.airport,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
}), FlashMobileStickyHeaderFilters)
